<header class="complaint-header">
  <a routerLink="/welcome" class="complaint-brand">
    <img class="complaint-logo" src="assets/images/OHA-Color.png" width="1500" height="495" alt="Oregon Health Authority">
    <span class="portal-brand">{{titleFull}}</span>
    <span class="visually-hidden"> — portal home</span>
  </a>
</header>

<div class="portal-prose flex flex-col p-6 gap-8 items-center" *ngIf="complaint.id === 0 && displayStart && !showSuccess" [@slideRight]>
  <h1 class="portal-page-title">Health Facility Licensing Complaints</h1>
  <h2 class="text-2xl">Select the type of facility you would like to file a complaint against</h2>
  <div class="flex flex-col items-center md:flex-row md:gap-4 text-xl">
    <div class="flex flex-col gap-4 items-center">
      <span>In Home Care Agency</span>
      <button type="button" mat-flat-button color="primary" (click)="confirmNewComplaint()">Start IHC Complaint</button>
    </div>
    <div class="flex flex-col gap-4 items-center">
      <span>Other Facility Types</span>
      <a href="https://www.healthoregon.org/facilitycomplaints" mat-flat-button color="primary">For All Other Complaints</a>
    </div>
  </div>
</div>

<div class="flex flex-col portal-prose px-4 text-xl pt-16" *ngIf="complaint.id > 0 && !updatingClientNarrative" [@slideRight]>
  <div class="flex flex-col gap-4 pb-8 items-center">
    <span class="text-4xl font-semibold">In Home Care Complaint Intake Form</span>
  </div>

  <div class="flex flex-col gap-4">
    <span>
      Thank you for sharing your concerns with the Office of Health Care Regulation and Quality Improvement. Our program
      investigates patient health and safety issues in acute and continuing care settings.
    </span>
    <span>
      The information you provide will be reviewed against the applicable Oregon Administrative rules for In-Home Care Agencies.
      The review will determine if there are potential violations of those requirements and if our office has jurisdiction to take further action.
    </span>
    <span>
      You will be notified in writing of the results of the review. The letter will inform you what action this office has the authority to take,
      which may include an unannounced, on-site investigation. If it is determined that the concerns fall under the jurisdiction of another agency
       or organization, the letter will provide you with that information.
    </span>
    <span>
      Your identity as the complainant is maintained confidentially to the extent permitted by law. Our office is prohibited from releasing
      complainant information for most complaints, and the complaint systems are designed to protect that anonymity.
    </span>
    <div class="flex flex-col">
      <span>Please complete this form as thoroughly as possible. If you have any questions, please call (971)673-0540, option 3, or email</span>
      <a href="mailto:mailbox.inhomecare@odhsoha.oregon.gov">mailbox.inhomecare&#64;odhsoha.oregon.gov</a>
    </div>
  </div>

  <div class="flex flex-col pt-8">
    <span class="font-semibold pb-4">Select the agency that you would like to file a complaint against:</span>
    <form [formGroup]="complaintForm" (ngSubmit)="submitDocuments()">
      <div class="flex flex-col">

        <mat-form-field class="md:w-1/3">
          <mat-label>Agency</mat-label>
          <mat-select formControlName="entity" (selectionChange)="selectAgency($event.value)">
            <mat-option *ngFor="let agency of ihcAgencies" [value]=agency.agencyName>{{agency.agencyName}} {{agency.city}}</mat-option>
            <mat-option value="Other">-- Other --</mat-option>
          </mat-select>
        </mat-form-field>

        <div class="flex flex-col" *ngIf="complaintForm.get('entity')?.value === 'Other'">
          <span>Please specify the agency name if "Other" is selected:</span>
          <mat-form-field class="md:w-1/3">
            <mat-label>Agency Name</mat-label>
            <input matInput formControlName="otherEntity" name="otherEntity" />
          </mat-form-field>
        </div>

        <span class="font-semibold pb-4">What is your name, mailing address, and contact information?</span>

        <div class="flex flex-col md:flex-row gap-2">
          <mat-form-field class="md:w-1/3">
            <mat-label>First Name</mat-label>
            <input matInput formControlName="firstName">
          </mat-form-field>

          <mat-form-field class="md:w-1/3">
            <mat-label>Middle Name</mat-label>
            <input matInput formControlName="middleName">
          </mat-form-field>

          <mat-form-field class="md:w-1/3">
            <mat-label>Last Name</mat-label>
            <input matInput formControlName="lastName">
          </mat-form-field>
        </div>

        <div class="flex flex-col md:flex-row gap-2">
          <mat-form-field class="md:w-1/3">
            <mat-label>Phone</mat-label>
            <input matInput formControlName="phone" mask="000-000-0000">
          </mat-form-field>

          <mat-form-field class="md:w-1/3">
            <mat-label>Email</mat-label>
            <input matInput formControlName="email">
            <mat-error *ngIf="complaintForm.get('email')?.invalid">
              Email is invalid
            </mat-error>
          </mat-form-field>
        </div>

        <mat-form-field class="md:w-1/3">
          <textarea matInput placeholder="Address" type="text" rows="3" formControlName="complainantAddress"></textarea>
        </mat-form-field>

        <div class="flex flex-col md:flex-row gap-4">
          <div class="flex flex-col">
            <span class="font-semibold pb-4">What is your employee status with this agency? (This information to be used for internal administrative purposes only.)</span>
            <mat-form-field>
              <mat-label></mat-label>
              <input matInput formControlName="employeeStatus" name="employeeStatus" />
            </mat-form-field>
          </div>

          <mat-checkbox formControlName="neverAnEmployee" labelPosition="before">
            <span class="font-semibold pb-4">I have never been an employee of this agency</span>
          </mat-checkbox>
        </div>

        <div class="flex flex-col md:flex-row md:items-center gap-4 py-4">
          <div class="flex flex-col md:w-1/2">
            <label id="work-for-aps" class="font-semibold">
              Do you work for APS or another Oregon agency?
            </label>
            <mat-radio-group aria-labelledby="work-for-aps" formControlName="workForAPS">
              <mat-radio-button [value]="true">
                Yes
              </mat-radio-button>
              <mat-radio-button [value]="false">
                No
              </mat-radio-button>
            </mat-radio-group>
          </div>

          <mat-form-field class="w-full md:w-1/2" *ngIf="complaintForm.get('workForAPS')?.value">
            <mat-label>Which Agency?</mat-label>
            <input matInput formControlName="apsAgency">
          </mat-form-field>
        </div>

        <div class="flex flex-col gap-2 md:w-1/2">
          <label id="complainant-is-client" class="font-semibold">
            Are you filing this complaint on behalf of yourself as a client of the agency?
          </label>
          <mat-radio-group aria-labelledby="complainant-is-client"formControlName="complainantIsClient">
            <mat-radio-button [value]="true">
              Yes
            </mat-radio-button>
            <mat-radio-button [value]="false">
              No
            </mat-radio-button>
          </mat-radio-group>
        </div>

        <app-affected-person *ngIf="complaintForm.get('complainantIsClient')?.value !== null" class="py-8"
                             [isClient]="complaintForm.get('complainantIsClient')?.value ?? false"
                             [personDetails]="{firstName: complaintForm.get('firstName')?.value ?? '',
                                               middleName: complaintForm.get('middleName')?.value ?? '',
                                               lastName: complaintForm.get('lastName')?.value ?? '',
                                               address: complaintForm.get('complainantAddress')?.value ?? '',
                                               phone: complaintForm.get('phone')?.value ?? '',
                                               email: complaintForm.get('email')?.value ?? ''}"></app-affected-person>

        <span class="font-semibold py-4">What were the dates and times that the incident(s) occurred?</span>
        <mat-form-field>
          <textarea matInput placeholder="Incident Dates" type="text" rows="3" formControlName="incidentDates"></textarea>
        </mat-form-field>

        <span class="font-semibold pb-4 ">To summarize, what do you believe the agency did wrong?</span>
        <mat-form-field class="md:w-1/3">
          <mat-label>Summary</mat-label>
          <mat-select formControlName="summary" multiple (selectionChange)="onSummaryChange($event.value)">
            <mat-option value="Client Abuse">Client Abuse (including financial)</mat-option>
            <mat-option value="Client Neglect">Client Neglect</mat-option>
            <mat-option value="Client Injury">Client Injury</mat-option>
            <mat-option value="Client Death">Client Death</mat-option>
            <mat-option value="Services are not being provided">Services are not being provided</mat-option>
            <mat-option value="Quality of care">Quality of care</mat-option>
            <mat-option value="Caregivers are not staffing scheduled shifts">Caregivers are not staffing scheduled shifts</mat-option>
            <mat-option value="Caregivers are inadequately trained">Caregivers are inadequately trained</mat-option>
            <mat-option value="Caregiver has an inadequate background check">Caregiver has an inadequate background check</mat-option>
            <mat-option value="Termination rights">Termination rights</mat-option>
            <mat-option value="False billing">False billing</mat-option>
            <mat-option value="Falsification of records">Falsification of records</mat-option>
            <mat-option value="Complaints to the agency were unaddressed">Complaints to the agency were unaddressed</mat-option>
            <mat-option value="Unlicensed Business/Agency providing IHC services">Unlicensed Business/Agency providing IHC services</mat-option>
            <mat-option value="Infection control">Infection control</mat-option>
            <mat-option value="Nursing services">Nursing services</mat-option>
            <mat-option value="Medication services">Medication services</mat-option>
            <mat-option value="Other">Other</mat-option>
          </mat-select>
        </mat-form-field>

        <div class="flex flex-col" *ngIf="getSummaryArray().length > 0">
          <span class="text-lg text-amber-600">Please ensure you include details for the following:</span>
          <span class="text-amber-600" *ngFor="let summary of getSummaryArray()">{{ summary }}</span>
        </div>

        <span class="font-semibold py-4">Please describe what happened in detail.</span>
        <mat-form-field>
          <textarea matInput placeholder="Description" type="text" rows="10" formControlName="description"></textarea>
        </mat-form-field>

        <mat-form-field class="md:w-1/3" *ngIf="getSummaryArray().includes('Other')">
          <mat-label>Summary Details</mat-label>
          <input matInput formControlName="summaryOther">
        </mat-form-field>

        <span class="font-semibold pb-4">Does anyone else have first-hand knowledge of the incidents? Such as agency staff, volunteers, family members, other clients, or visitors? Please
          list the names, relationship/title and if you know it, their contact information.
        </span>
        <mat-form-field>
          <textarea matInput placeholder="Others with Knowledge" type="text" rows="5" formControlName="othersWithKnowledge"></textarea>
        </mat-form-field>

        <span class="font-semibold pb-4">Was there a complaint submitted to the IHC agency? If so, please provide the calendar date, employee who received the complaint,
          and the report number if available. If the agency responded please provide the date of the response, who responded, and how they responded(mail, email, phone, or in person)
        </span>
        <mat-form-field>
          <textarea matInput placeholder="Complaint Submitted to Agency" type="text" rows="5" formControlName="complaintSubmittedAgency"></textarea>
        </mat-form-field>

        <span class="font-semibold pb-4">Did you report this to another state agency, including aps/dhs? If so, please provide the calendar date, employee who received the complaint,
          and the report number if available. If the agency responded please provide the date of the response, who responded, and how they responded(mail, email, phone, or in person)</span>
        <mat-form-field>
          <textarea matInput placeholder="Reported to Another Agency" type="text" rows="5" formControlName="complaintSubmittedOther"></textarea>
        </mat-form-field>

        <span class="font-semibold pb-4">Did you report this to police? If so, please list the name of the officer, report number, and police precinct.
          If there were actions or findings related to the complaint, please describe them here.
        </span>
        <mat-form-field>
          <textarea matInput placeholder="Reported to Police" type="text" rows="5" formControlName="complaintSubmittedPolice"></textarea>
        </mat-form-field>

        <span class="font-semibold pb-4">Please upload any supporting documents related to this complaint.</span>

        <app-public-document class="md:w-1/2"
                             [entityId]="complaint.id"
                             [entityType]="sharedService.complaint"
                             [canUpload]="true"
                             [stageDocuments]="true"
                             (uploadsComplete)="submitComplaint()">
        </app-public-document>

        <div class="flex flex-col md:flex-row md:gap-4 py-8 ml-auto">
          <button type="submit" mat-flat-button color="primary" [disabled]="submitting">
            <ng-container *ngIf="!submitting; else submittingComplaint">
              Submit Complaint
            </ng-container>
            <ng-template #submittingComplaint>
              <svg class="h-5 w-5 animate-spin" viewBox="0 0 24 24" aria-hidden="true">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" fill="none"></circle>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"></path>
              </svg>
              <span class="sr-only">Submitting complaint</span>
            </ng-template>
          </button>
          <button type="button" mat-flat-button color="warn" (click)="cancelComplaint()">Cancel</button>
        </div>
      </div>
    </form>
  </div>
</div>

<div class="flex flex-col pt-8 text-xl" style="font-family: Oswald, sans-serif;" *ngIf="complaint.id > 0 && updatingClientNarrative && !showSuccess" [@slideRight]>
  <div class="max-w-6xl w-full mx-auto p-4">
    <div class="flex flex-col md:flex-row md:gap-4">
      <!-- Complainant Info -->
      <div class="flex flex-col w-full md:w-1/2 p-8 rounded shadow">
        <h3 class="text-2xl font-semibold pb-2">Complainant</h3>
        <div class="space-y-2 text-lg">
          <div class="flex flex-col md:flex-row">
            <span class="font-medium md:w-1/3">Name: </span>
            <span>{{ complaint?.firstName }} {{ complaint?.middleName }} {{ complaint?.lastName }}</span>
          </div>
          <div class="flex flex-col md:flex-row">
            <span class="font-medium md:w-1/3">Phone: </span>
            <span>{{ complaint?.phone }}</span>
          </div>
          <div class="flex flex-col md:flex-row">
            <span class="font-medium md:w-1/3">Email: </span>
            <span>{{ complaint?.email }}</span>
          </div>
          <div class="flex flex-col md:flex-row">
            <span class="font-medium md:w-1/3">Address: </span>
            <span>{{ complaint?.complainantAddress }}</span>
          </div>
          <div class="flex flex-col md:flex-row">
            <span class="font-medium md:w-1/3">Agency Employee: </span>
            <span>{{ complaint?.employeeStatus }}</span>
          </div>
          <div class="flex flex-col md:flex-row">
            <span class="font-medium md:w-1/3">Never an employee: </span>
            <span>{{ complaint?.neverAnEmployee ? 'Yes' : 'No' }}</span>
          </div>
          <div class="flex flex-col md:flex-row">
            <span class="font-medium md:w-1/3">Works for APS/State agency: </span>
            <span>{{ complaint?.workForAPS === true ? 'Yes' : (complaint?.workForAPS === false ? 'No' : '') }}</span>
          </div>
        </div>
      </div>

      <!-- Affected Person Info -->
      <div class="flex flex-col w-full md:w-1/2 p-8 rounded shadow">
        <h3 class="text-2xl font-semibold pb-2">Affected Person</h3>
        <div class="space-y-2 text-lg" *ngIf="complaint && complaint.affectedPeople && complaint.affectedPeople.length > 0">
          <div class="flex flex-col md:flex-row">
            <span class="font-medium md:w-1/3">Name: </span>
            <span>{{ complaint.affectedPeople[0]?.firstName }} {{ complaint.affectedPeople[0]?.middleName }} {{ complaint.affectedPeople[0]?.lastName }}</span>
          </div>
          <div class="flex flex-col md:flex-row">
            <span class="font-medium md:w-1/3">Phone: </span>
            <span>{{ complaint.affectedPeople[0]?.phone ?? '' | mask: '(000) 000-0000' }}</span>
          </div>
          <div class="flex flex-col md:flex-row">
            <span class="font-medium md:w-1/3">Email: </span>
            <span>{{ complaint.affectedPeople[0]?.email }}</span>
          </div>
          <div class="flex flex-col md:flex-row">
            <span class="font-medium md:w-1/3">Address: </span>
            <span>{{ complaint.affectedPeople[0]?.address }}</span>
          </div>
          <div class="flex flex-col md:flex-row">
            <span class="font-medium md:w-1/3">Relationship: </span>
            <span>{{ complaint.affectedPeople[0]?.relationship }}</span>
          </div>
          <div class="flex flex-col md:flex-row" *ngIf="complaint.affectedPeople[0]?.relationship === 'Other'">
            <span class="font-medium md:w-1/3">Relationship Other: </span>
            <span>{{ complaint.affectedPeople[0]?.relationshipOther }}</span>
          </div>
        </div>
      </div>
    </div>

    <div class="flex flex-col md:flex-row md:gap-4">
      <!-- Agency & Incident Info -->
      <div class="flex flex-col w-full md:w-1/2 p-8 rounded shadow">
        <h3 class="text-2xl font-semibold pb-2">Agency Information</h3>
        <div class="space-y-2 text-lg">
          <div class="flex flex-col md:flex-row">
            <span class="font-medium md:w-1/2">Agency: </span>
            <span>{{ complaint?.entity === 'Other' ? complaint?.otherEntity : complaint?.entity }}</span>
          </div>
          <div class="flex flex-col md:flex-row">
            <span class="font-medium md:w-1/2">Affected person: </span>
            <span *ngIf="complaint?.affectedPeople && complaint.affectedPeople.length > 0">{{ complaint.affectedPeople[0]?.firstName }} {{ complaint.affectedPeople[0]?.middleName }} {{ complaint.affectedPeople[0]?.lastName }}</span>
          </div>
          <div class="flex flex-col md:flex-row">
            <span class="font-medium md:w-1/2">Incident dates/times: </span>
            <span>{{ complaint?.incidentDates }}</span>
          </div>
          <div class="flex flex-col md:flex-row">
            <span class="font-medium md:w-1/2">Others with knowledge: </span>
            <span>{{ complaint?.othersWithKnowledge }}</span>
          </div>
          <div class="flex flex-col md:flex-row">
            <span class="font-medium md:w-1/2">Reported to agency: </span>
            <p class="mt-1">{{ complaint?.complaintSubmittedAgency }}</p>
          </div>
        </div>
      </div>

      <!-- Description & Summary (displayed for quick reference) -->
      <div class="flex flex-col w-full md:w-1/2">
        <div class="mt-4 p-8 rounded shadow">
          <h3 class="text-2xl font-semibold pb-2">Description & Summary</h3>
          <div class="text-lg">
            <div>
              <span class="font-semibold text-xl">Description: </span>
              <p class="mt-1">{{ complaint?.description }}</p>
            </div>
            <div class="pt-8 flex flex-col">
              <span class="font-semibold text-xl">Summary: </span>
              <p class="mt-1">{{ getSummaryArray()?.join(', ') }}</p>
            </div>
            <div class="pt-8" *ngIf="complaint?.summaryOther">
              <span class="font-semibold text-xl">Summary Details: </span>
              <p class="mt-1">{{ complaint?.summaryOther }}</p>
          </div>
        </div>

        <!-- Additional details full width -->
        <div class="mt-4 p-8 rounded shadow space-y-4">
          <h3 class="text-2xl font-semibold">Other Reports & Actions</h3>
          <div class="text-lg">
            <div>
              <span class="font-semibold text-xl">Reported to other agency:</span>
              <p class="mt-1">{{ complaint?.complaintSubmittedOther }}</p>
            </div>
            <div class="pt-4">
              <span class="font-semibold text-xl">Reported to police:</span>
              <p class="mt-1">{{ complaint?.complaintSubmittedPolice }}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- Client narrative editable area -->
  <div class="pt-4">
    <h3 class="text-2xl font-semibold pb-4">Enter Your Updates</h3>
    <mat-form-field class="w-full">
      <textarea matInput rows="6" placeholder="Enter your updates" [(ngModel)]="clientNarrative" name="clientUpdates"></textarea>
    </mat-form-field>
  </div>

  <div class="flex flex-col md:flex-row md:gap-4">
    <button type="button" mat-flat-button color="primary" (click)="saveClientNarrative()">Save</button>
  </div>
  </div>
</div>

<div class="flex flex-col portal-prose px-4 text-xl" style="font-family: Oswald, sans-serif;" *ngIf="showSuccess" [@slideRight]>
  <div class="flex flex-col gap-4 pt-8 items-center">
    <h1 class="text-4xl">Thank you for submitting your complaint.</h1>
    <h2 class="text-2xl">You will receive a confirmation email shortly.</h2>
    <span>Please include the complaint Id {{complaint.complaintId}} for any inquiries regarding this complaint.</span>
  </div>
</div>

<div class="flex flex-col portal-prose px-4 text-xl" style="font-family: Oswald, sans-serif;" *ngIf="showError" [@slideRight]>
  <div class="flex flex-col gap-4 pt-8 items-center">
    <h1 class="text-4xl">An error occurred while submitting your complaint.</h1>
    <h2 class="text-2xl">Please try again later or contact us for assistance.</h2>
  </div>
</div>


